import type { IdeaRowDTO } from "@roomate/core";

/** The lead-time clock on an idea card: filed → merged, or how long it has been building so far. */
export type IdeaClock = { kind: "shipped"; ms: number } | { kind: "none" } | { kind: "running"; ms: number; merging: boolean };

/**
 * The idea has finished building: its intent is done, or it has tasks and every one is done or cancelled.
 * A dropped idea (see isDropped) finishes building too, so this alone does not mean shipped.
 */
export function hasFinishedBuilding(row: Pick<IdeaRowDTO, "intent" | "tasks">): boolean {
  const { intent, tasks } = row;
  return intent.status === "done" || (tasks.length > 0 && tasks.every((t) => t.stage === "done" || t.stage === "cancelled"));
}

/** Every task was cancelled: nothing was built, so the idea is dropped, never shipped. */
export function isDropped(row: Pick<IdeaRowDTO, "tasks">): boolean {
  return row.tasks.length > 0 && row.tasks.every((t) => t.stage === "cancelled");
}

/**
 * Decision table, in order:
 * 1. shippedAt set → shipped, measured from filing to the last merge.
 * 2. intent cancelled, or intent done with no live (non-cancelled) task → none: abandoned or nothing to time, so no clock.
 * 3. anything else, including a draft with no tasks → running. `merging`: finished building and not dropped, not yet shipped.
 */
export function ideaClock(row: IdeaRowDTO, now: number): IdeaClock {
  const { intent, tasks, shippedAt } = row;
  if (shippedAt != null) return { kind: "shipped", ms: shippedAt - intent.createdAt };
  const live = tasks.some((t) => t.stage !== "cancelled");
  if (intent.status === "cancelled" || (intent.status === "done" && !live)) return { kind: "none" };
  // A dropped idea (nothing built) has no merge pending.
  return { kind: "running", ms: now - intent.createdAt, merging: hasFinishedBuilding(row) && !isDropped(row) };
}
